import { PenLineIcon, PlusIcon, ReceiptTextIcon, SearchIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"
import { DocumentStatusBadge, useStatusLabel } from "@/features/invoices/DocumentStatusBadge"
import { DraftsStrip } from "@/features/drafts/DraftsStrip"
import { ActiveFilterChips, InvoiceFilterSheet } from "@/features/invoices/InvoiceFilterSheet"
import { PaymentFilterSelect } from "@/features/payments/PaymentFilterSelect"
import { UnpaidRegister } from "@/features/payments/UnpaidRegister"
import {
  INVOICE_PAGE_SIZE,
  useInvoiceFilters,
  useInvoiceList,
  type InvoiceSortKey,
} from "@/features/invoices/use-invoice-list"
import { SyncStatus } from "@/components/SyncStatus"
import { SortableHead } from "@/components/SortableHead"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { useFormatDay } from "@/lib/dates"
import type { InvoiceListRow } from "@/lib/endpoints"
import { ApiError } from "@/lib/api-error"
import { formatRupees } from "@/lib/money"
import { routes } from "@/routes/paths"

/**
 * The Bills register (§11.2 "all bills", §17.4).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * CARDS ON A PHONE, A TABLE ON A DESKTOP — one data source, two layouts. The
 * owner works from a phone in a workshop (§11.1); a five-column table there
 * either scrolls sideways or shrinks the type below the 16px floor, and both
 * make the number column unreadable. The son reconciles on a laptop, where a
 * table is the right tool. Neither layout is a cut-down version of the other:
 * they carry the same fields.
 *
 * CANCELLED BILLS ARE IN THIS LIST, DELIBERATELY (§17.4). A cancelled invoice
 * keeps its number and stays in the book — hiding it would make the register
 * look like the bill never happened, and would put a visible gap in a gap-free
 * series (§6). It is marked, not removed. This is the opposite of the quotation
 * register, where VOID is hidden by default (§4).
 *
 * PAYMENT IS SHOWN ONLY WHERE THE LEDGER SAYS SO (§17.5, which closed §11.2's
 * known gap on 2026-08-16). This note used to read "nothing here implies payment
 * — the app does not know", and the second half is what has changed: there is a
 * `payments` ledger now, and outstanding is derived from it by the server on
 * every read.
 *
 * The first half still holds for the DEFAULT view. `GET /invoices` returns no
 * payment information at all — `InvoiceListRow` carries no received or
 * outstanding figure — so the ordinary register shows none, and must not guess
 * one from anything on the row. Payment appears on this screen in exactly one
 * place: the `unpaid=true` view, which is a different request against a
 * different endpoint and says so (see `UnpaidRegister`). A register is exactly
 * where a wrong guess would be believed, which is why the rule is "show what the
 * ledger returned" rather than "show a status".
 *
 * NO EDIT AFFORDANCE ANYWHERE. An issued invoice is not amendable and
 * `invoice.edit` does not exist as a capability for anyone (§2.1). A row opens
 * the document; correction is a credit note (§17.3) or a cancellation (§17.4).
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function InvoiceListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()
  const formatDay = useFormatDay()
  const statusLabel = useStatusLabel()

  const { filters, setFilters, clearFilters, activeCount } = useInvoiceFilters()

  /*
   * ═════════════════════════════════════════════════════════════════════════
   * §17.5's UNPAID FILTER LIVES IN THE URL LIKE EVERY OTHER FILTER, AND IS
   * EXCLUSIVE OF THEM ALL.
   *
   * It is read straight from the query string rather than through
   * `useInvoiceFilters`, because it is not a parameter of `GET /invoices`:
   * `InvoiceListQuerySchema` is `.strict()` and would answer 400 for it. What it
   * selects is a DIFFERENT REQUEST — `GET /payments/outstanding` — because
   * "still owed" is `grand_total_paise` − `SUM(payments.amount_paise)`, an
   * aggregate over another table that this register's `where` cannot express.
   *
   * That endpoint takes no parameters at all: no dates, no customer, no amount
   * range, no paging. So turning the filter on REPLACES the query string rather
   * than merging into it, and while it is on the search box, the status select
   * and the "More filters" sheet are not rendered. A date range left visibly set
   * while it silently did nothing would be a filter that lies, and this codebase
   * already keeps `list-filter-parity.test.ts` because a register's filter and
   * what the register shows must agree.
   *
   * The home screen's "who owes me money" card links to `routes.invoicesUnpaid`
   * — `/invoices?unpaid=true` — so the figure and the list it opens are the same
   * rows from the same producer.
   * ═════════════════════════════════════════════════════════════════════════
   */
  const [params, setParams] = useSearchParams()
  const unpaidOnly = params.get("unpaid") === "true"
  const setUnpaidOnly = (next: boolean) => {
    setParams(next ? new URLSearchParams({ unpaid: "true" }) : new URLSearchParams(), {
      replace: true,
    })
  }

  const query = useInvoiceList(filters, { enabled: !unpaidOnly })

  /* Sorting is a filter like any other: it goes in the URL and returns to page
   * one, because holding offset 150 while re-ordering shows a page of rows that
   * were not there a moment ago. */
  const sortBy = (sort: InvoiceSortKey | undefined, dir: "asc" | "desc" | undefined) => {
    setFilters({ sort, dir })
  }

  const rows = query.data?.invoices ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.invoices")}</h1>
          <p className="text-muted-foreground text-sm">
            {unpaidOnly ? t("payment.registerSubtitle") : t("invoice.listSubtitle")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* The register is the screen most likely to be stale — a bill his
            * son raised on the shop computer will not appear here until
            * something refetches.
            *
            * Absent in the unpaid view, which is fed by a different query and
            * carries its OWN sync control: this one would report the freshness
            * of a request that is not running, which is a lie with a timestamp
            * on it. */}
          {unpaidOnly ? null : (
            <SyncStatus
              updatedAt={query.dataUpdatedAt}
              syncing={query.isFetching}
              onSync={() => {
                void query.refetch()
              }}
            />
          )}
          {can("invoice.create") ? (
            /*
             * `nativeButton={false}` because this renders an `<a>`, not a
             * `<button>`. Base UI assumes a native button unless told otherwise
             * and warns at runtime that "rendering a non-<button> removes native
             * button semantics". It is a link — it navigates, it belongs in the
             * accessibility tree as a link, and it should middle-click and
             * open-in-new-tab like one.
             */
            <Button nativeButton={false} render={<Link to={routes.invoiceNew} />}>
              <PlusIcon />
              {t("btn.newInvoice")}
            </Button>
          ) : null}
        </div>
      </header>

      {/* §7.2 — unfinished bills, above the register because "continue what I
        * was doing" beats "browse what is done" every time it applies. Only for
        * someone who can create; a viewer has no business resuming a form.
        *
        * Hidden in the unpaid view: a half-typed form has no number, no total
        * and nothing owed on it, so it cannot be part of an answer to "who owes
        * me money". */}
      {can("invoice.create") && !unpaidOnly ? <DraftsStrip kind="INVOICE" /> : null}

      <div className="flex flex-col gap-3">
        <InvoiceFilterBar
          filters={filters}
          setFilters={setFilters}
          clearFilters={clearFilters}
          activeCount={activeCount}
          unpaidOnly={unpaidOnly}
          setUnpaidOnly={setUnpaidOnly}
        />
        {/* What is currently narrowing the list, removable one at a time. The
          * unpaid view has no other filter on by construction — see the note in
          * the component — so there is nothing to chip. */}
        {unpaidOnly ? null : <ActiveFilterChips filters={filters} setFilters={setFilters} />}
      </div>

      {unpaidOnly ? (
        /* A DIFFERENT REQUEST, NOT A NARROWER ONE — see the note at the top of
         * this component and the header of `UnpaidRegister`. */
        <UnpaidRegister />
      ) : query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <RegisterSkeleton />
      ) : rows.length === 0 ? (
        /*
         * TWO DIFFERENT EMPTIES, and telling them apart is the whole point. "You
         * have no bills" on a register filtered to last March would be a lie
         * that sends the owner looking for lost documents; "nothing matches"
         * with a way back out is the truth.
         */
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ReceiptTextIcon />
            </EmptyMedia>
            <EmptyTitle>
              {activeCount > 0 ? t("list.noneMatch") : t("invoice.emptyTitle")}
            </EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("list.noneMatchHelp") : t("invoice.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : can("invoice.create") ? (
              <Button nativeButton={false} render={<Link to={routes.invoiceNew} />}>
                <PlusIcon />
                {t("btn.newInvoice")}
              </Button>
            ) : null}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          {/* Phone: one card per bill, the whole row a tap target. */}
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((row) => (
                <li key={row.id} className="border-b last:border-b-0">
                  <Link
                    to={routes.invoice(row.id)}
                    aria-label={t("list.rowLabel", {
                      number: row.number,
                      customer: row.customerName ?? t("lbl.customerUnknown"),
                      date: formatDay(row.date),
                      status: statusLabel(row.status, "invoice"),
                    })}
                    className="hover:bg-accent/50 flex min-h-14 items-center gap-3 p-4 transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium tabular-nums">{row.number}</span>
                        <DocumentStatusBadge status={row.status} kind="invoice" className="text-xs" />
                        <HandWrittenMark row={row} />
                      </span>
                      <span className="truncate text-sm text-muted-foreground">
                        {row.customerName ?? t("lbl.customerUnknown")}
                      </span>
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {formatDay(row.date)}
                      </span>
                    </span>
                    <span className="shrink-0 font-medium tabular-nums">
                      {formatRupees(row.grandTotalPaise)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          {/* Desktop: the register as a book. */}
          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  {/* Every column the API can order by, and no others — a
                    * header that looks sortable and is not is worse than a
                    * plain one. `sortBy` puts the choice in the URL, so a
                    * sorted register is linkable (see use-invoice-list). */}
                  <SortableHead column="number" label={t("lbl.number")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="date" label={t("lbl.date")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="customer" label={t("lbl.customer")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="status" label={t("lbl.status")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                  <SortableHead column="total" align="end" label={t("lbl.total")} sort={filters.sort} dir={filters.dir} onSort={sortBy} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  /*
                   * `relative` IS LOAD-BEARING. The number cell's link stretches
                   * over the whole row with `after:absolute after:inset-0`, and
                   * an absolutely-positioned pseudo-element resolves against its
                   * nearest POSITIONED ancestor. Without `relative` here that is
                   * the page itself, so the overlay covers the entire viewport
                   * and every click anywhere on screen opens the last bill in
                   * the list. The whole row is the target — reachable by
                   * keyboard, middle-clickable, openable in a new tab, because
                   * it is a real anchor and not a row `onClick`.
                   */
                  <TableRow key={row.id} className="hover:bg-accent/50 relative cursor-pointer">
                    <TableCell className="font-medium tabular-nums">
                      <Link
                        to={routes.invoice(row.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {row.number}
                      </Link>
                      <HandWrittenMark row={row} className="ml-2" />
                    </TableCell>
                    <TableCell className="tabular-nums whitespace-nowrap">
                      {formatDay(row.date)}
                    </TableCell>
                    <TableCell className="max-w-[24ch] truncate">
                      {row.customerName ?? (
                        <span className="text-muted-foreground">{t("lbl.customerUnknown")}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <DocumentStatusBadge status={row.status} kind="invoice" />
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                      {formatRupees(row.grandTotalPaise)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              // Paging with the list scrolled halfway down lands the owner in
              // the middle of the next page with no idea it changed.
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/** §6.1 — a bill that came out of the hand-written book, not out of the app. */
function HandWrittenMark({
  row,
  className,
}: {
  row: InvoiceListRow
  className?: string
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (!row.issuedManually) return null

  return (
    <span
      className={`text-muted-foreground inline-flex items-center gap-1 text-xs ${className ?? ""}`}
      title={t("invoice.writtenByHand")}
    >
      <PenLineIcon aria-hidden="true" className="size-3" />
      <span className="sr-only">{t("invoice.writtenByHand")}</span>
    </span>
  )
}

/**
 * The filters the API actually supports — and no others.
 *
 * THE STATUS IS NO LONGER A NATIVE `<select>` (2026-08-16). The owner asked for
 * the app's own Select here, for visual consistency with every other control on
 * the screen, and that is his call. The reason the native one was chosen is kept
 * rather than deleted, because a deleted rationale is how the next reader
 * reverts a decision: on a phone a native `<select>` opens the operating
 * system's own full-height wheel — bigger than anything drawn in the page,
 * already familiar, and reliably hittable WITHOUT the page having to draw a
 * 48px target (§11.1). A custom listbox looks tidier in a screenshot and is
 * worse in a workshop unless that target is drawn by hand.
 *
 * SO IT IS DRAWN BY HAND: `` on every `SelectItem` and on the trigger.
 * That is 44px, and it is the rem class rather than `min-h-[44px]` on purpose —
 * `styles/preferences.css` scales the root font size for the large-text
 * preference precisely so touch targets grow with it, and a pixel literal would
 * stay stuck at 44 for the person who most needed it bigger. THAT CLASS IS THE
 * WHOLE REASON THIS MIGRATION IS NOT A REGRESSION IN A WORKSHOP. Do not tidy it
 * away; the same pair sits on the quotation and challan registers.
 *
 * THE OPTION LIST IS ALSO PASSED AS `items`. Base UI resolves the trigger's text
 * from that array — with no `items` it prints the raw value, so choosing
 * "Cancelled" would put the Prisma enum `CANCELLED` on screen, in English, on a
 * Marathi phone.
 *
 * DATES ARE NO LONGER NATIVE (2026-08-15). `<input type="date">` renders in the
 * BROWSER's locale, so a Marathi screen opened an English picker and every
 * platform drew a different control. `components/DateField` uses the app's own
 * calendar with month and weekday names from `en.json` / `mr.json` (§11.4).
 * The trade is real and was made deliberately: the OS picker is a better target
 * on a phone, and one consistent, translated calendar is worth more than that.
 */
function InvoiceFilterBar({
  filters,
  setFilters,
  clearFilters,
  activeCount,
  unpaidOnly,
  setUnpaidOnly,
}: {
  filters: ReturnType<typeof useInvoiceFilters>["filters"]
  setFilters: ReturnType<typeof useInvoiceFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
  /** §17.5 — the register is showing the outstanding list, not `GET /invoices`. */
  unpaidOnly: boolean
  setUnpaidOnly: (next: boolean) => void
}): React.JSX.Element {
  const { t } = useTranslation()

  /*
   * Exactly the four InvoiceStatus values (prisma/schema.prisma), plus "no
   * filter". The API validates against the same enum and answers 400 for
   * anything else, so an invented option here would be a dead filter.
   */
  const statusOptions = [
    { value: "", label: t("list.anyStatus") },
    { value: "ISSUED", label: t("status.issued") },
    { value: "DRAFT", label: t("status.draft") },
    { value: "CANCELLED", label: t("status.cancelled") },
    { value: "PENDING_IRN", label: t("status.pendingIrn") },
  ]

  /*
   * IN THE UNPAID VIEW, THE PAYMENT SELECT IS THE ONLY CONTROL ON THE BAR.
   *
   * Everything else here narrows `GET /invoices`, and the unpaid view is not
   * that request — see the note at the top of `InvoiceListScreen`. Leaving a
   * status select and a date sheet on screen while they narrowed nothing would
   * be worse than removing them: the owner would set one, watch the list not
   * change, and stop trusting the filters that DO work. The way back to the full
   * register is the same select he arrived by.
   */
  if (unpaidOnly) {
    return (
      <div className="flex flex-wrap items-end gap-3">
        <PaymentFilterSelect unpaidOnly onChange={setUnpaidOnly} />
      </div>
    )
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      {/*
        * SEARCH OVER THE DOCUMENT'S OWN NUMBERS — the invoice number, the S.O.,
        * the challan, the quotation reference. It is a SERVER search (`?q=`), so
        * it covers the whole book rather than the fifty rows on this page.
        *
        * It does not match the customer name: that lives in a text-backed JSON
        * column the server will not query inside (CLAUDE.md). Finding a
        * customer's bills is the customer filter, which is exact.
        */}
      <InvoiceSearchBox filters={filters} setFilters={setFilters} />

      <div className="flex min-w-40 flex-col gap-1.5">
        <Label htmlFor="filter-status">{t("list.statusFilter")}</Label>
        <Select
          items={statusOptions}
          value={filters.status ?? ""}
          onValueChange={(value) => {
            /*
             * "" MEANS NO FILTER, and it has to leave as `undefined` rather than
             * as an empty parameter: the list schema is `.strict()`, so `?status=`
             * is a 400 and not a no-op. Base UI hands the value straight over —
             * there is no `event.target` — and types it nullable, so the null is
             * folded into the same branch.
             */
            setFilters({ status: value === "" || value == null ? undefined : String(value) })
          }}
        >
          {/* The id lands on the trigger button, which is what `<Label htmlFor>`
            * above then labels — a <button> is a labelable element, so
            * `getByLabelText("Status")` still finds this control. */}
          <SelectTrigger id="filter-status" className="w-full">
            {/* Same string as the "" item: Base UI treats an empty value as NO
              * value, so without this the trigger would read as a greyed-out
              * placeholder instead of "Any status". */}
            <SelectValue placeholder={t("list.anyStatus")} />
          </SelectTrigger>
          {/* `w-auto` because the popup otherwise matches the trigger exactly and
            * clips the longer labels — the OS wheel sized itself. */}
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {statusOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} className="pl-3">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/*
        * §17.5 — "All bills / Still owed", BESIDE the status select and not
        * inside it. A payment state is not a document status: a bill is ISSUED
        * *and* part paid, and folding the two onto one axis would make "issued
        * and unpaid" unaskable. It hides itself entirely when the module is off
        * or the caller lacks `payment.view` (§18.3, §2.1).
        */}
      <PaymentFilterSelect unpaidOnly={false} onChange={setUnpaidOnly} />

      {/*
        * EVERYTHING ELSE LIVES BEHIND ONE BUTTON. Status stays out here because
        * it is the filter reached for most; dates, customer and the §6.1
        * hand-written flag are a dialog away, which keeps the toolbar usable on
        * a phone instead of wrapping into three rows of date inputs.
        */}
      <InvoiceFilterSheet
        filters={filters}
        setFilters={setFilters}
        clearFilters={clearFilters}
        activeCount={activeCount}
      />
    </div>
  )
}

/**
 * "Showing 1–50 of 214", with the two moves that matter.
 *
 * OFFSET PAGING, matching the API (`limit`/`offset`). No page numbers: with 214
 * bills there would be five, and with 3,000 there would be sixty — a number line
 * nobody uses. Previous and Next are what a register needs.
 */
/**
 * The number search.
 *
 * DEBOUNCED, and mirrored in local state rather than read straight from the URL.
 * Writing every keystroke to the URL fires a request per character and re-renders
 * the whole screen through the router, which on a phone makes the field feel
 * like it is dropping letters.
 */
function InvoiceSearchBox({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useInvoiceFilters>["filters"]
  setFilters: ReturnType<typeof useInvoiceFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  // Follow the URL when it changes elsewhere — back button, "clear filters".
  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-xs">
      <Label htmlFor="invoice-search">{t("list.searchNumbers")}</Label>
      <div className="relative">
        <SearchIcon
          aria-hidden="true"
          className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
        />
        <Input
          id="invoice-search"
          type="search"
          className="pl-9 tabular-nums"
          placeholder={t("list.searchNumbersPlaceholder")}
          value={text}
          onChange={(event) => {
            setText(event.target.value)
          }}
        />
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= INVOICE_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - INVOICE_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + INVOICE_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function RegisterSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-40" />
            </div>
            <Skeleton className="h-4 w-24" />
          </li>
        ))}
      </ul>
    </Card>
  )
}

/**
 * Unfinished bills (§7.2). Absent entirely when there are none — an empty
 * "drafts" box would push the register down for nothing every day.
 */
